import React, { useEffect, useMemo, useRef, useState } from "react"
import { Check, ChevronDown } from "lucide-react"
import { fieldControlClassName } from "../ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover"
import { cn } from "../../lib/utils"
import type { SearchableSelectOption } from "./SearchableSelect"

const LEGACY_FIELD_CLASS = /(?:^|\s)(?:cm-input|fin-input|um-role-select)(?:\s|$)/
const SHADCN_FIELD_CLASS = /\bh-\d/

export type SearchableMultiSelectProps = {
  value: string[]
  options: SearchableSelectOption[]
  onChange: (value: string[]) => void
  placeholder?: string
  disabled?: boolean
  error?: boolean
  searchPlaceholder?: string
  emptyMessage?: string
  noOptionsMessage?: string
  className?: string
  id?: string
  menuPortal?: boolean
  "aria-label"?: string
  filterOption?: (option: SearchableSelectOption, query: string) => boolean
}

function formatSelectedLabel(
  selectedOptions: SearchableSelectOption[],
  placeholder: string,
): string {
  if (selectedOptions.length === 0) return placeholder
  if (selectedOptions.length === 1) return selectedOptions[0].label
  if (selectedOptions.length === 2) {
    return `${selectedOptions[0].label}, ${selectedOptions[1].label}`
  }
  return `${selectedOptions.length} selected`
}

const SearchableMultiSelect: React.FC<SearchableMultiSelectProps> = ({
  value,
  options,
  onChange,
  placeholder = "Select…",
  disabled = false,
  error = false,
  searchPlaceholder = "Search…",
  emptyMessage = "No options available.",
  noOptionsMessage = "No options match your search.",
  className = "",
  id,
  menuPortal = true,
  "aria-label": ariaLabel,
  filterOption,
}) => {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const searchRef = useRef<HTMLInputElement>(null)

  const selectedOptions = useMemo(
    () => options.filter((option) => value.includes(option.value)),
    [options, value],
  )

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return options
    return options.filter((option) => {
      if (filterOption) return filterOption(option, query)
      const haystack = `${option.label} ${option.searchText ?? ""}`.toLowerCase()
      return haystack.includes(query)
    })
  }, [options, search, filterOption])

  const usesLegacyFieldStyle = LEGACY_FIELD_CLASS.test(className)
  const usesShadcnFieldStyle =
    !usesLegacyFieldStyle && (SHADCN_FIELD_CLASS.test(className) || className.includes("rounded-md"))

  const triggerClassName = cn(
    "flex w-full items-center justify-between gap-2 whitespace-nowrap text-left text-sm transition-colors focus:outline-none disabled:cursor-not-allowed disabled:opacity-50",
    usesLegacyFieldStyle
      ? [
          "h-auto min-h-0 rounded-[inherit] border-0 bg-transparent px-[inherit] py-[inherit] shadow-none focus:ring-0",
          className,
          error && "err",
        ]
      : usesShadcnFieldStyle
        ? [fieldControlClassName, error && "border-destructive", className]
        : [
            "cm-input h-auto min-h-0 rounded-[inherit] border-0 bg-transparent px-[inherit] py-[inherit] shadow-none focus:ring-0",
            error && "err",
            className,
          ],
    open && !usesShadcnFieldStyle && "open",
    open && usesShadcnFieldStyle && "ring-1 ring-ring",
  )

  const triggerLabel = formatSelectedLabel(selectedOptions, placeholder)
  const hasSelection = selectedOptions.length > 0

  useEffect(() => {
    if (!open) return
    // Avoid autofocus on touch UIs — keyboard + viewport resize dismisses the menu.
    const isTouchUi =
      typeof window !== "undefined" &&
      window.matchMedia("(pointer: coarse)").matches
    if (isTouchUi) return

    const frame = window.requestAnimationFrame(() => {
      searchRef.current?.focus({ preventScroll: true })
    })
    return () => window.cancelAnimationFrame(frame)
  }, [open])

  const handleToggle = (nextValue: string) => {
    if (value.includes(nextValue)) {
      onChange(value.filter((item) => item !== nextValue))
      return
    }
    onChange([...value, nextValue])
  }

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
        if (!nextOpen) setSearch("")
      }}
      modal={false}
    >
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          disabled={disabled}
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={triggerClassName}
        >
          <span
            className={cn(
              "min-w-0 flex-1 truncate text-left",
              hasSelection ? "text-foreground" : "text-muted-foreground",
            )}
          >
            {triggerLabel}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        portal={menuPortal}
        align="start"
        className="w-[var(--radix-popover-trigger-width)] p-0"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div className="border-b border-border bg-popover p-2">
          <input
            ref={searchRef}
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            className="flex h-8 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm outline-none transition-colors placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-ring"
            onKeyDown={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
          />
        </div>

        {options.length === 0 ? (
          <p className="px-3 py-2 text-sm text-muted-foreground">{emptyMessage}</p>
        ) : filtered.length === 0 ? (
          <p className="px-3 py-2 text-sm text-muted-foreground">{noOptionsMessage}</p>
        ) : (
          <div
            className="max-h-60 overflow-y-auto p-1"
            role="listbox"
            aria-multiselectable
          >
            {filtered.map((option) => {
              const isSelected = value.includes(option.value)
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className="flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                  onClick={() => handleToggle(option.value)}
                >
                  {/* Decorative only — Radix Checkbox renders a <button>, which cannot nest here. */}
                  <span
                    aria-hidden
                    className={cn(
                      "flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-primary shadow",
                      isSelected && "bg-primary text-primary-foreground",
                    )}
                  >
                    {isSelected ? <Check className="h-3.5 w-3.5" /> : null}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{option.label}</span>
                </button>
              )
            })}
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

export default SearchableMultiSelect
